import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "modal__container" }
const _hoisted_2 = {
  key: 0,
  class: "modal__container modal__container--free-column"
}

import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { computed } from 'vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem } from '@/coreDomainsModule/constant/validateRules';
import { ref, watch } from 'vue';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import AddOptionSection from './AddOptionSection.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { storeToRefs } from 'pinia';
import { validateRuleKeyMap, keyMappingForSelectInput, formOptionsBusinessCreate, inputTypeKeyMap } from '../constants';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { objectDiff } from '@/utils/objectDiff';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { useUpdateFieldState } from '../composables/useUpdateFieldState';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';
import { diContainer } from '@/coreContainer/container';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateBaseBusinessModal',
  props: {
    item: { default: undefined }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);
const { getAddableOptions } = store;

const props = __props;

const emits = __emit;

const isEdited = ref<boolean>(false);

const fieldItems = computed((): FieldItem[] => {
  const targetArray = formOptionsBusinessCreate;
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState, isEdited);

const currentOpenSelectInputName = ref('');
const baseFreeColumnSettings = ref<Omit<BusinessFreeColumnValue, 'value'>[]>([]);
const businessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);

/**
 * 自由欄設定（関連取引）を取得し、設定済みの項目だけを保持する。columnNo は 1 から採番する。
 */
const fetchBaseFreeColumnSettings = async () => {
  const result = await diContainer.settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    baseFreeColumnSettings.value = [];
    return;
  }
  baseFreeColumnSettings.value = result.status.data.base
    .map((item, index) => ({
      columnNo: index + 1,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      target: 'base' as const,
    }))
    .filter((item) => item.isConfigured);
};

/**
 * 自由欄の入力欄を編集対象の現在値でプリフィルする
 */
const initializeBusinessFreeColumns = () => {
  const stateValues = state.value.item?.businessFreeColumnValues;
  const sourceValues = stateValues?.some((column) => column.target === 'base') ? stateValues : props.item?.businessFreeColumnValues;
  businessFreeColumnValues.value = baseFreeColumnSettings.value.map((setting) => ({
    ...setting,
    value:
      sourceValues?.find((column) => (column.target === 'base' || column.target === undefined) && column.columnNo === setting.columnNo)
        ?.value ?? '',
  }));
};

/**
 * 自由欄の入力値を更新する
 * @param index - 自由欄配列のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  const target = businessFreeColumnValues.value[index];
  if (!target) return;
  target.value = value;
  isEdited.value = true;
};

const baseFreeColumnSettingsReady = fetchBaseFreeColumnSettings();

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (allValidate()) {
    const baseBusinessResponsibleUsersString = fieldState['baseBusinessResponsibleUser'].value.value;
    const aggregateFreeColumnValues = state.value.item?.businessFreeColumnValues?.filter((column) => column.target === 'aggregate') ?? [];
    const updatedItem = BusinessFactory.create({
      ...props.item,
      warehouseNumber: fieldState['warehouseNumber'].value.value,
      warehouse: fieldState['warehouseName'].value.value,
      supplierNumber: fieldState['supplierNumber'].value.value,
      supplierName: fieldState['supplierName'].value.value,
      businessNumber: fieldState['businessNumber'].value.value,
      businessName: fieldState['businessName'].value.value,
      package: fieldState['package'].value.value,
      customerNumber: fieldState['customerNumber'].value.value,
      customerName: fieldState['customerName'].value.value,
      salesDestinationNumber: fieldState['salesDestinationNumber'].value.value,
      salesDestinationName: fieldState['salesDestinationName'].value.value,
      endUser: updateBusinessVM.convertUnassingedToUndefined(fieldState['endUser'].value.value),
      baseBusinessResponsibleUsers: baseBusinessResponsibleUsersString
        ? baseBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
        : [],
      businessFreeColumnValues: [
        ...aggregateFreeColumnValues.map((column) => ({ ...column })),
        ...businessFreeColumnValues.value.map((column) => ({ ...column })),
      ],
    });
    const updatedDiff = objectDiff(props.item ?? {}, updatedItem);
    state.value.updatedItemDiff = updatedDiff.first;

    state.value.item = updatedItem;
    emits('close', 'baseBusinessEdit', true);
  }
};

/**
 * モーダルを開いた際の処理
 */
watch(
  () => [state.value.modalState.baseBusinessEdit, state.value.isCreateMode],
  async ([stateNewVal, isCreateModeNewBal]) => {
    if (stateNewVal && isCreateModeNewBal && props.item) {
      await baseFreeColumnSettingsReady;
      if (!state.value.modalState.baseBusinessEdit || !state.value.isCreateMode) return;
      resetForm(fieldItems.value);
      for (let i = 0; i < fieldItems.value.length; i++) {
        const keyName = fieldItems.value[i].name;
        if (keyName === 'warehouseName') {
          fieldState[keyName].setValue(state.value.item?.['warehouse'] ?? props.item['warehouse'], false);
        } else if (keyName === 'baseBusinessResponsibleUser') {
          fieldState[keyName].setValue(
            state.value.item?.baseBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
              props.item.baseBusinessResponsibleUsers?.map((user: UserAccount) => user.email).join(',') ??
              '',
            false
          );
        } else {
          fieldState[keyName].setValue(state.value.item?.[keyName] ?? props.item[keyName] ?? '', false);
        }
      }
      initializeBusinessFreeColumns();
    }
  }
);

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 */
const getBaseDataItemClass = (option: string) => {
  if (option === 'package' || option === 'baseBusinessResponsibleUser') {
    return 'modal__item--number';
  } else {
    return 'modal__item--normal';
  }
};

/**
 * 自由入力カラムの編集状態を変更する
 */
const markBusinessFreeColumnEdited = () => {
  isEdited.value = true;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(InfoModal, {
    "modal-state": _unref(state).modalState.baseBusinessEdit && _unref(state).isCreateMode,
    "modal-width": 1300,
    "back-button-label": _unref(t)('キャンセル'),
    "next-button-label": _unref(t)('保存'),
    "is-disabled-next-button": !isEdited.value,
    onOnBack: _cache[0] || (_cache[0] = 
      () => {
        emits('close', 'baseBusinessEdit', false);
      }
    ),
    onOnNext: editingModalOnNext
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('取引追加')), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('新規に取引を追加できます。')), 1)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formOptionsBusinessCreate), (option, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: option,
            class: _normalizeClass(["modal__item", getBaseDataItemClass(option)])
          }, [
            (_unref(inputTypeKeyMap)[option] === 'selectUser')
              ? (_openBlock(), _createBlock(MultipleUserSelectInput, {
                  key: 0,
                  "select-input-index": index,
                  label: _unref(t)(option),
                  placeholder: _unref(t)(option),
                  "original-selected-user-id-list": _unref(fieldState)[option].value.value ? _unref(fieldState)[option].value.value.split(',') : [],
                  "onUpdate:modelValue": (e) => _unref(updateUserValue)(option, e)
                }, null, 8, ["select-input-index", "label", "placeholder", "original-selected-user-id-list", "onUpdate:modelValue"]))
              : (_openBlock(), _createBlock(SingleSelectInput, {
                  key: 1,
                  index: index,
                  label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                  "model-value": _unref(fieldState)[option].value.value,
                  placeholder: _unref(t)(option),
                  "filter-type": _unref(keyMappingForSelectInput)[option],
                  "is-error": _unref(fieldState)[option]?.isError.value,
                  "error-message": _unref(fieldState)[option]?.errorMessage.value,
                  "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                  "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "onClick:selectInput", "onUpdate:modelValue"]))
          ], 2))
        }), 128))
      ]),
      (businessFreeColumnValues.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(businessFreeColumnValues.value, (freeColumn, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: freeColumn.columnNo,
                class: "modal__item"
              }, [
                (freeColumn.isMulti)
                  ? (_openBlock(), _createBlock(MultiTagInput, {
                      key: 0,
                      "model-value": freeColumn.value,
                      label: freeColumn.headerName,
                      placeholder: freeColumn.headerName,
                      onEditing: markBusinessFreeColumnEdited,
                      "onUpdate:modelValue": (e) => updateBusinessFreeColumnValue(index, e)
                    }, null, 8, ["model-value", "label", "placeholder", "onUpdate:modelValue"]))
                  : (_openBlock(), _createBlock(TextInput, {
                      key: 1,
                      "model-value": freeColumn.value,
                      label: freeColumn.headerName,
                      placeholder: freeColumn.headerName,
                      "ensure-error-msg-height": "",
                      "onUpdate:modelValue": (e) => updateBusinessFreeColumnValue(index, e)
                    }, null, 8, ["model-value", "label", "placeholder", "onUpdate:modelValue"]))
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createVNode(AddOptionSection, {
        options: _unref(getAddableOptions)(false),
        onSetOption: _unref(addNewOption)
      }, null, 8, ["options", "onSetOption"])
    ]),
    _: 1
  }, 8, ["modal-state", "back-button-label", "next-button-label", "is-disabled-next-button"]))
}
}

})